/**
 * Copyright 2015-present Singlepoint. All Rights Reserved.
 *
 * @flow
 */

import './ManageInsuranceProductPage.scss';

import { TitleWithUnderLine } from '@boi/core/lib';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext } from 'react';
import type { Match, RouterHistory } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { CognitoContext } from '../../CognitoUtils';
import ButtonWithIcons from '../../components/ButtonWithIcons/ButtonWithIcons';
import { MobileBackButton } from '../../components/MobileBackButton';
import { HOME_QUOTE_STORAGE, iconsAndTitles } from '../../constants';
import { dustyGrayColor } from '../../constants/colors';
import {
  CAR_INSURANCE_TYPE,
  HOME_INSURANCE_TYPE,
  TRAVEL_INSURANCE_TYPE
} from '../../constants/insuranceTypeConstants';
import { POLICY_RENEWAL } from '../../constants/sessionStorage/genericStorageIdentifiers';
import {
  getObjectFromSessionStorage,
  removeFromSessionStorage
} from '../../helpers';
import { insuranceTypeParameter } from '../../helpers';
import { routes } from '../../routes';

const RightArrowIcon = () => (
  <FontAwesomeIcon icon={faChevronRight} color={dustyGrayColor} />
);

type Props = {
  history: RouterHistory,
  location: Location,
  match: Match
};

const ManageInsuranceProductPage = ({ match, history }: Props) => {
  const className = 'c-ManageInsuranceProductPage';
  const insuranceType = insuranceTypeParameter(match);
  const policyRenewal = getObjectFromSessionStorage(POLICY_RENEWAL);
  const { feature } = useContext(CognitoContext);

  const renderBreakdownAssistanceButton = () => {
    return insuranceType === CAR_INSURANCE_TYPE ? (
      <div className={`${className}__buttonSpacing`}>
        <Link to={`${routes.breakdownAssistance.url}${insuranceType}`}>
          <ButtonWithIcons
            id={'ManageInsuranceProductPage__breakdownAssistanceButton'}
            IconRight={RightArrowIcon}
          >
            Breakdown assistance
          </ButtonWithIcons>
        </Link>
      </div>
    ) : (
      <></>
    );
  };

  const renderMedicalAssistanceButton = () => {
    return insuranceType === TRAVEL_INSURANCE_TYPE ? (
      <div className={`${className}__buttonSpacing`}>
        <Link to={`${routes.medicalAssistance.url}${insuranceType}`}>
          <ButtonWithIcons
            id={'ManageInsuranceProductPage__medicalAssistanceButton'}
            IconRight={RightArrowIcon}
          >
            Medical assistance
          </ButtonWithIcons>
        </Link>
      </div>
    ) : (
      <></>
    );
  };

  const renderViewDocumentsButton = () => (
    <div className={`${className}__buttonSpacing`}>
      <Link to={`${routes.policyLinks.url}${insuranceType}`}>
        <ButtonWithIcons
          id={'ManageInsuranceProductPage__viewDocumentsButton'}
          IconRight={RightArrowIcon}
        >
          View documents
        </ButtonWithIcons>
      </Link>
    </div>
  );

  const renderViewRenewalsButton = () =>
    insuranceType == HOME_INSURANCE_TYPE ? (
      <div className={`${className}__buttonSpacing`}>
        {Object.keys(getObjectFromSessionStorage(HOME_QUOTE_STORAGE)).length !==
          0 && removeFromSessionStorage(HOME_QUOTE_STORAGE)}
        <Link to={`${routes.viewRenewalQuote.url}${insuranceType}`}>
          <ButtonWithIcons
            id={'ManageInsuranceProductPage__viewDocumentsButton'}
            IconRight={RightArrowIcon}
          >
            View Renewals
          </ButtonWithIcons>
        </Link>
      </div>
    ) : (
      <></>
    );

  const showRenewalButton = () =>
    policyRenewal?.renewal && feature?.flags?.home_renewal_enabled;

  return (
    <div className={className}>
      <div className={`${className}__backButtonDesktop`}>
        <MobileBackButton history={history} />
      </div>
      {insuranceType && iconsAndTitles[insuranceType] ? (
        <div className={`${className}__iconContainer`}>
          <div className={`${className}__icon`}>
            {iconsAndTitles[insuranceType].icon}
          </div>
          <div className={`${className}__titleText`}>
            <TitleWithUnderLine>
              {iconsAndTitles[insuranceType].title}
            </TitleWithUnderLine>
          </div>
        </div>
      ) : (
        <></>
      )}
      <div className={`${className}__buttonContainer`}>
        {showRenewalButton() && renderViewRenewalsButton()}
        {renderViewDocumentsButton()}
        {renderBreakdownAssistanceButton()}
        {renderMedicalAssistanceButton()}
        <div className={`${className}__buttonSpacing`}>
          <Link to={`${routes.submitClaimPage.url}${insuranceType}`}>
            <ButtonWithIcons
              id={'ManageInsuranceProductPage__submitClaimButton'}
              IconRight={RightArrowIcon}
            >
              Submit a claim
            </ButtonWithIcons>
          </Link>
        </div>
        <div className={`${className}__buttonSpacing`}>
          <Link to={`${routes.amendPolicyPage.url}${insuranceType}`}>
            <ButtonWithIcons
              id="ManageInsuranceProductPage__amendMyPolicyButton"
              IconRight={RightArrowIcon}
            >
              Amend my policy
            </ButtonWithIcons>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ManageInsuranceProductPage;
